import { useCallback, useEffect, useMemo, useState } from "react";
import * as FC from "@fluentui/react-components";
import * as IC from "@fluentui/react-icons";
import { SimpleGrid, useSimpleGrid } from "../../components/simple_grid";
import { useDrawer } from "../../components/use_drawer";
import { bindInputValue, useInfo } from "../../form_utils";
import { CustomerDef } from "../../services/customer_service";
import { apiClient } from "../../services/api";
import { useToast } from "../../hooks/useToast";
import { PASSWORD_POLICY_HINT, isPasswordAcceptable } from "../../password_policy";

const useStyles = FC.makeStyles({
    root: {
        display: "flex",
        flexDirection: "column",
        ...FC.shorthands.gap("10px"),
        ...FC.shorthands.padding("10px"),
        height: "100%",
        boxSizing: "border-box",
    },
    form: {
        display: "flex",
        flexDirection: "column",
        ...FC.shorthands.gap("10px"),
    },
    twoColumn: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        ...FC.shorthands.gap("10px"),
    },
    muted: { color: FC.tokens.colorNeutralForeground3 },
    emptyState: {
        display: "flex",
        flexDirection: "column",
        ...FC.shorthands.gap("6px"),
        ...FC.shorthands.padding("16px"),
        ...FC.shorthands.border("1px", "dashed", FC.tokens.colorNeutralStroke2),
        ...FC.shorthands.borderRadius(FC.tokens.borderRadiusMedium),
    },
});

const normalizeCustomer = (customer) => ({
    id: customer?.id,
    tenantId: customer?.tenantId,
    tenantName: customer?.tenantName,
    assetOwnerId: customer?.assetOwnerId,
    assetOwnerName: customer?.assetOwnerName,
    account: customer?.account,
    name: customer?.name,
    companyName: customer?.companyName,
    contactEmail: customer?.contactEmail,
    contactPhone: customer?.contactPhone,
    address: customer?.address,
    description: customer?.description,
    customerCode: customer?.customerCode,
    disabled: Boolean(customer?.disabled),
});

/**
 * Separator between a tenant's account prefix and the name an operator types.
 * Mirrors `TenantAccountPrefix.Separator` on the backend, which is authoritative.
 */
export const ACCOUNT_PREFIX_SEPARATOR = ".";

/**
 * The account that will actually be stored for `name` under `prefix`.
 *
 * The operator types a name inside their own tenant; the server prepends the prefix so that
 * `Users.Account` stays globally unique without anyone having to know what other tenants used.
 * Showing the composed value here is the whole point — an operator who types `zgh` should not have
 * to submit the form to discover they got `clototics.zgh`.
 *
 * Returns "" when there is nothing meaningful to show yet, so callers can test it as a boolean.
 */
export const composeCustomerAccount = (prefix, name) => {
    const cleanPrefix = (prefix || "").trim().toLowerCase();
    const typed = (name || "").trim();
    if (!cleanPrefix || !typed) return "";
    // Accept a value the operator pasted back with the prefix already on it, exactly as the
    // backend does, so the preview does not read `clototics.clototics.zgh`.
    const bare = typed.toLowerCase().startsWith(cleanPrefix + ACCOUNT_PREFIX_SEPARATOR)
        ? typed.slice(cleanPrefix.length + 1)
        : typed;
    return bare ? `${cleanPrefix}${ACCOUNT_PREFIX_SEPARATOR}${bare}` : "";
};

/**
 * Turns `/api/admin/tenants/{id}/asset-owners` rows into the options this form may offer.
 *
 * Two things this must not get wrong, both of which produce a page that looks fine:
 *
 * 1. **Field names.** That endpoint returns `assetOwnerId` / `assetOwnerSeq` / `assetOwnerName` —
 *    it projects the ACCESS row, not the owner. Reading `id`/`seq`/`name` yields a dropdown of
 *    "undefined - undefined" that can never produce a selected id.
 * 2. **Which rows are selectable.** `effectiveStatus` collapses Status and the validity window but
 *    does NOT look at `scopeType`, so `effectiveStatus === "active"` alone is a weaker test than the
 *    rule the backend enforces on save. Both conditions are applied here, so the candidate list is
 *    the same set the server will accept instead of a superset that 400s on submit.
 */
export const toSelectableOwners = (rows) =>
    (rows || [])
        .filter((row) => row?.effectiveStatus === "active" && row?.scopeType === "all_assets")
        .map((row) => ({
            id: row.assetOwnerId,
            seq: row.assetOwnerSeq,
            name: row.assetOwnerName,
        }))
        .filter((owner) => owner.id != null);

/**
 * Create/edit form for one tenant's customer account.
 *
 * The tenant is a read-only property of the page, never a form field: this form can only ever write
 * into the tenant whose workbench it is mounted in. On edit the asset owner is read-only too —
 * re-binding a customer would silently re-home the inspection sessions that point at it, so the
 * backend refuses it with 400 ImmutableField and the UI must not pretend otherwise.
 */
export const TenantCustomerForm = ({
    item,
    tenantId,
    tenantName,
    accountPrefix = "",
    assetOwners,
    onChange,
}) => {
    const styles = useStyles();
    const normalized = useMemo(() => normalizeCustomer(item), [item]);
    const isEdit = Boolean(normalized.id);
    const { info, setInfo, updateInfo } = useInfo({});
    const [isSaving, setIsSaving] = useState(false);
    const showToast = useToast();

    useEffect(() => {
        setInfo({});
    }, [item, setInfo]);

    const v = bindInputValue(normalized, info, setInfo);
    const selectedAssetOwnerId = (info.assetOwnerId ?? normalized.assetOwnerId ?? "").toString();
    const selectedOwnerName =
        assetOwners.find((owner) => owner.id?.toString() === selectedAssetOwnerId)?.name ||
        normalized.assetOwnerName ||
        "";

    const composedAccount = composeCustomerAccount(accountPrefix, info?.account);

    const close = () => onChange && onChange();

    const handleSave = async () => {
        if (isSaving) return;
        try {
            setIsSaving(true);
            if (isEdit) {
                const payload = {};
                ["name", "companyName", "contactEmail", "contactPhone", "address", "description"]
                    .forEach((key) => {
                        if (info?.[key] !== undefined) payload[key] = info[key];
                    });
                if (Object.keys(payload).length === 0) {
                    close();
                    return;
                }
                await apiClient.updateManagedCustomer(tenantId, normalized.id, payload);
                showToast("客户信息已更新", "success");
                close();
                return;
            }

            const name = info?.name?.trim();
            const companyName = info?.companyName?.trim();
            const contactEmail = info?.contactEmail?.trim();
            const password = info?.password?.trim();
            if (!info?.account?.trim() || !name || !companyName || !contactEmail || !password) {
                showToast("请填写账号、联系人姓名、公司名称、邮箱与初始密码", "warning");
                return;
            }
            if (!accountPrefix) {
                // The backend answers TenantAccountPrefixNotConfigured here. Saying so before the
                // request is sent means the operator does not lose the rest of the form to it.
                showToast("该租户尚未设置账号前缀，请先在租户信息中设置后再创建客户", "warning");
                return;
            }
            // Send the composed account. The backend composes it again from the same rule and
            // accepts a value that already carries this tenant's prefix, so the two agree either
            // way; sending it makes what the operator was shown and what is stored the same string.
            const account = composedAccount;
            if (!isPasswordAcceptable(password)) {
                showToast(`初始${PASSWORD_POLICY_HINT}`, "warning");
                return;
            }
            if (!selectedAssetOwnerId) {
                showToast("请选择该客户绑定的业主", "warning");
                return;
            }

            await apiClient.createManagedCustomer(tenantId, {
                tenantId,
                assetOwnerId: Number(selectedAssetOwnerId),
                account,
                name,
                companyName,
                contactEmail,
                password,
                contactPhone: info?.contactPhone?.trim() || null,
                address: info?.address?.trim() || null,
                description: info?.description?.trim() || null,
            });
            showToast("客户创建成功", "success");
            close();
        } catch (error) {
            console.error("Failed to save customer:", error);
            showToast("保存客户信息失败", "error", error.message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleToggle = async (enable) => {
        if (!isEdit || isSaving) return;
        if (!window.confirm(enable ? "确认启用该客户？" : "确认禁用该客户？禁用后客户无法登录系统。")) {
            return;
        }
        try {
            setIsSaving(true);
            if (enable) {
                await apiClient.enableManagedCustomer(tenantId, normalized.id);
            } else {
                await apiClient.disableManagedCustomer(tenantId, normalized.id);
            }
            showToast(enable ? "客户已启用" : "客户已禁用", "success");
            close();
        } catch (error) {
            console.error("Failed to change customer status:", error);
            showToast(enable ? "启用客户失败" : "禁用客户失败", "error", error.message);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className={styles.form}>
            <FC.Input readOnly contentBefore="所属租户" value={tenantName || `Tenant ${tenantId}`} />
            <div className={styles.twoColumn}>
                {isEdit ? (
                    <FC.Input readOnly contentBefore="绑定业主" value={selectedOwnerName || "-"} />
                ) : (
                    <FC.Dropdown
                        placeholder={
                            assetOwners.length
                                ? "选择绑定业主"
                                : "该租户当前没有有效的业主可见范围"
                        }
                        disabled={assetOwners.length === 0}
                        selectedOptions={selectedAssetOwnerId ? [selectedAssetOwnerId] : []}
                        value={selectedOwnerName}
                        onOptionSelect={(_, data) => updateInfo({ assetOwnerId: data.optionValue })}
                    >
                        {assetOwners.map((owner) => (
                            <FC.Option key={owner.id} value={owner.id?.toString()}>
                                {`${owner.seq} - ${owner.name}`}
                            </FC.Option>
                        ))}
                    </FC.Dropdown>
                )}
                {isEdit ? (
                    <FC.Input readOnly contentBefore="客户账号" value={normalized.account || "-"} />
                ) : (
                    <FC.Field
                        hint={
                            accountPrefix
                                ? `登录账号为 ${composedAccount || `${accountPrefix}${ACCOUNT_PREFIX_SEPARATOR}<账号名>`}，前缀由租户自动添加`
                                : "该租户尚未设置账号前缀，无法创建客户账号"
                        }
                        validationState={accountPrefix ? "none" : "warning"}
                    >
                        <FC.Input
                            contentBefore="客户账号"
                            contentAfter={
                                accountPrefix ? (
                                    <span className={styles.muted}>
                                        {accountPrefix}
                                        {ACCOUNT_PREFIX_SEPARATOR}
                                    </span>
                                ) : undefined
                            }
                            placeholder="仅填账号名，例如 zgh"
                            {...v("account")}
                        />
                    </FC.Field>
                )}
                <FC.Input contentBefore="联系人姓名" {...v("name")} />
                <FC.Input contentBefore="公司名称" {...v("companyName")} />
                <FC.Input contentBefore="联系邮箱" type="email" autoComplete="off" {...v("contactEmail")} />
                <FC.Input contentBefore="联系电话" {...v("contactPhone")} />
                <FC.Input contentBefore="公司地址" {...v("address")} />
                {!isEdit && (
                    <FC.Field hint={PASSWORD_POLICY_HINT}>
                        <FC.Input
                            contentBefore="初始密码"
                            type="password"
                            autoComplete="new-password"
                            {...v("password")}
                        />
                    </FC.Field>
                )}
            </div>
            <FC.Textarea resize="vertical" placeholder="备注" {...v("description")} />
            {isEdit && (
                <div className={styles.muted}>
                    绑定业主与所属租户不可在此修改。需要纠正时请停用后重新创建。
                </div>
            )}
            <FC.Divider />
            <div style={{ display: "flex", gap: 10 }}>
                {isEdit && !normalized.disabled && (
                    <FC.Button appearance="secondary" disabled={isSaving} onClick={() => handleToggle(false)}>
                        禁用
                    </FC.Button>
                )}
                {isEdit && normalized.disabled && (
                    <FC.Button appearance="secondary" disabled={isSaving} onClick={() => handleToggle(true)}>
                        启用
                    </FC.Button>
                )}
                <FC.Button appearance="primary" onClick={handleSave} disabled={isSaving}>
                    {isEdit ? "保存" : "创建"}
                </FC.Button>
            </div>
        </div>
    );
};

/**
 * What an empty customer list says.
 *
 * An empty table and deleted data look identical, and on 2026-08-13 an operator read one as the
 * other: the front end had sent `search=undefined`, the backend matched that literal string, and
 * the page showed nothing with no indication that a search was in effect. So the empty state names
 * the tenant it looked in, names the search term if there was one, and offers a way to drop it.
 */
export const CustomerEmptyState = ({ tenantLabel, search, onClearSearch }) => {
    const styles = useStyles();
    if (search) {
        return (
            <div className={styles.emptyState}>
                <FC.Body1>没有匹配「{search}」的客户账号。</FC.Body1>
                <FC.Caption1 className={styles.muted}>
                    已在租户 {tenantLabel} 内检索。清除关键字可查看该租户的全部客户。
                </FC.Caption1>
                <div>
                    <FC.Button appearance="secondary" onClick={onClearSearch}>
                        清除搜索条件
                    </FC.Button>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.emptyState}>
            <FC.Body1>租户 {tenantLabel} 下还没有客户账号。</FC.Body1>
            <FC.Caption1 className={styles.muted}>
                该列表只显示本租户的客户；其他租户的客户不在此处，也不会被检索到。
            </FC.Caption1>
        </div>
    );
};

export const TenantCustomerManagement = ({
    tenantId = null,
    tenantName = "",
    accountPrefix = "",
}) => {
    const styles = useStyles();
    const fixedTenantId =
        Number.isInteger(Number(tenantId)) && Number(tenantId) > 0 ? Number(tenantId) : null;
    const [assetOwners, setAssetOwners] = useState([]);
    const [activeSearch, setActiveSearch] = useState("");
    // The grid starts with an empty item list before its first fetch resolves. Without this the
    // page would flash "this tenant has no customers" on every open — the same false statement the
    // empty state exists to stop being made.
    const [hasLoaded, setHasLoaded] = useState(false);

    useEffect(() => {
        if (!fixedTenantId) return;
        apiClient
            .getManagedAssetOwners(fixedTenantId)
            .then((items) => setAssetOwners(toSelectableOwners(items)))
            .catch((error) => console.error("Failed to load tenant asset owners:", error));
    }, [fixedTenantId]);

    const listItems = useCallback(
        async (_client, filter) => {
            if (!fixedTenantId) return [];
            const search = filter?.trim() || undefined;
            const rows =
                (await apiClient.getManagedCustomers(fixedTenantId, {
                    includeDisabled: true,
                    search,
                })) || [];
            // Both set after the await, so a failed request leaves the previous state rather than
            // claiming a result it never got. `search` is remembered because the empty state has to
            // say WHICH search found nothing: on 2026-08-13 the term was in the URL and nowhere on
            // the page, and the empty list was read as deleted data.
            setActiveSearch(search || "");
            setHasLoaded(true);
            return rows;
        },
        [fixedTenantId]
    );

    const { items, SearchInput, refresh, clearSearch } = useSimpleGrid({
        listItems,
        placeholder: "按客户编号、公司、联系人搜索...",
    });
    const { openDrawer, closeDrawer, drawer } = useDrawer();

    const onFormChange = () => {
        refresh();
        closeDrawer();
    };

    const openForm = (item) =>
        openDrawer({
            header: item ? `${item.companyName || item.account}` : "创建客户账号",
            body: (
                <TenantCustomerForm
                    item={item}
                    tenantId={fixedTenantId}
                    tenantName={tenantName}
                    accountPrefix={accountPrefix}
                    assetOwners={assetOwners}
                    onChange={onFormChange}
                />
            ),
        });

    const columns = [
        CustomerDef.createTableColumn("customerCode"),
        CustomerDef.createTableColumn("companyName"),
        CustomerDef.createTableColumn("assetOwnerName"),
        CustomerDef.createTableColumn("name"),
        CustomerDef.createTableColumn("contactEmail"),
        CustomerDef.createTableColumn("contactPhone"),
        CustomerDef.createTableColumn("disabled"),
        CustomerDef.createTableColumn("updatedAt"),
        CustomerDef.createOpsColumn({
            renderCell: (item) => (
                <FC.TableCellLayout>
                    <FC.Button
                        appearance="subtle"
                        icon={<IC.Edit16Regular />}
                        onClick={() => openForm(item)}
                    />
                </FC.TableCellLayout>
            ),
        }),
    ];

    if (!fixedTenantId) {
        return <div className={styles.root}>请先选择租户，再管理客户账号。</div>;
    }

    return (
        <div className={styles.root}>
            <div style={{ display: "flex", flexDirection: "row", gap: 10, alignItems: "center" }}>
                <FC.Label>快速检索</FC.Label>
                <SearchInput />
                <FC.Button appearance="primary" onClick={() => openForm(null)}>
                    创建客户账号
                </FC.Button>
                <span className={styles.muted}>
                    所属租户：{tenantName || `Tenant ${fixedTenantId}`}（不可更改）
                    {accountPrefix ? ` · 账号前缀 ${accountPrefix}${ACCOUNT_PREFIX_SEPARATOR}` : ""}
                </span>
            </div>
            <SimpleGrid items={items} columns={columns} />
            {hasLoaded && (items?.length ?? 0) === 0 && (
                <CustomerEmptyState
                    tenantLabel={tenantName || `Tenant ${fixedTenantId}`}
                    search={activeSearch}
                    onClearSearch={clearSearch}
                />
            )}
            {drawer}
        </div>
    );
};

export default TenantCustomerManagement;
